import React, { useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import api from '@disco/api/server'
import { logoutUser } from '@disco/state-management/actions/UserActions'
import { validateEmail } from '@disco/utils/validation'

import LoginMessage from 'components/common/login-message'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import TextField from 'components/ui/text-field'

import { useDispatch } from 'hooks/use-redux'
import styles from './index.module.css'

const ForgotPasswordComponent = (): React.ReactNode => {
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )
  const dispatch = useDispatch()
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  const validate = (): { isValid: boolean; error?: string } => {
    if (!value || value.trim() === '') {
      return { isValid: false, error: 'Invalid email address' }
    }

    if (!validateEmail(value.trim())) {
      return { isValid: false, error: 'Invalid email address' }
    }

    return { isValid: true }
  }

  const _onSubmit = () => {
    const validation = validate()
    if (validation.isValid) {
      _onValidate()
    } else {
      setError(validation.error || null)
    }
  }

  const _onValidate = useCallback(() => {
    let returnUrl

    try {
      // works only on https://auth.dsc.party, https://auth.disco.ac
      returnUrl = new URL(window.location.href).searchParams.get('return')
    } catch (err) {
      console.error('URL parsing error', err)
    }

    api.sendPasswordRecoveryInstructions(value.trim(), returnUrl)
    const from = location?.state?.from || searchParams.get('from')
    navigate(
      `/instructions-sent?email=${encodeURIComponent(value.trim())}${
        from ? '&from=' + from : ''
      }`,
    )
  }, [value, navigate, location, searchParams])

  const from = location?.state?.from || searchParams.get('from')
  let returnUrl = '/login'
  let returnMessage = 'Back to Sign in'

  if (from === 'settings') {
    returnUrl = '/settings/security'
    returnMessage = 'Back to Settings'
  }

  return (
    <LoginMessage helmet="Password recovery" title="Reset Password">
      <div className={styles.forgotPasswordContainer}>
        <TextField
          error={error}
          placeholder="Email"
          autoComplete="email"
          type="email"
          value={value}
          onChange={({ target }) => {
            setValue(target.value)
            setError(null) // Clear error when user starts typing
          }}
          compact={false}
        />
      </div>
      <ButtonContainer vertical>
        <Button
          appearance="primary"
          caption="Send me instructions"
          onClick={_onSubmit}
        />
        <Button
          link={returnUrl}
          caption={returnMessage}
          appearance="link"
          onClick={
            returnUrl === '/login' ? () => dispatch(logoutUser()) : undefined
          }
        />
      </ButtonContainer>
    </LoginMessage>
  )
}

export default ForgotPasswordComponent
